import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from '@/components/react/ui/carousel';

import { useEffect, useState } from 'react';

import AutoScroll from 'embla-carousel-auto-scroll';
import { PlayIcon } from './assets/icons/PlayIcon';
import { PauseIcon } from './assets/icons/PauseIcon';

import type { LogoMarquee as LogoMarqueeType } from '@/schemas/components/sections/logoMarqueeSchema';

type Props = Omit<LogoMarqueeType, '__component'>;

export const LogoMarquee = ({ items, bgColor }: Props) => {
  const [api, setApi] = useState<CarouselApi>();
  const [isPlaying, setIsPlaying] = useState(true);

  const handlePlayToggle = () => {
    setIsPlaying(!isPlaying);
  };

  useEffect(() => {
    if (isPlaying && api) {
      api.plugins().autoScroll.play();
    } else if (api) {
      api.plugins().autoScroll.stop();
    }
  }, [isPlaying, api]);

  const gradientClass =
    bgColor === 'White' ?
      'before:from-white before:to-transparent after:from-white after:to-transparent'
    : 'before:from-brand-purple-blue before:to-transparent after:from-brand-purple-blue after:to-transparent';

  return (
    <div className="relative max-w-full overflow-hidden">
      <Carousel
        setApi={setApi}
        opts={{
          align: 'start',
          loop: true,
          dragFree: true,
          containScroll: false,
          watchDrag: false,
        }}
        plugins={[
          AutoScroll({
            speed: 1,
            stopOnInteraction: false,
            stopOnMouseEnter: false,
          }),
        ]}
        className={`relative col-span-full before:absolute before:top-0 before:left-0 before:z-10 before:h-full before:w-[100px] before:bg-gradient-to-r after:absolute after:top-0 after:right-0 after:z-10 after:h-full after:w-[100px] after:bg-gradient-to-l ${gradientClass}`}
      >
        <CarouselContent className="-ml-4">
          {[...items, ...items, ...items, ...items].map((item, index) => {
            const isSvg = item.url.includes('.svg');

            return (
              <CarouselItem
                key={`${item.url}-${index}`}
                className="basis-auto pl-4"
              >
                <div className="flex h-[100px] w-[180px] items-center justify-center">
                  {isSvg ?
                    <svg height={80} width={160}>
                      <title>{item.alternativeText}</title>
                      <image xlinkHref={item.url} height={80} width={160} />
                    </svg>
                  : <img
                      className="max-h-[80px] max-w-[160px] object-contain"
                      src={item.url}
                      alt={item.alternativeText ?? ''}
                    />
                  }
                </div>
              </CarouselItem>
            );
          })}
        </CarouselContent>
      </Carousel>

      <button
        className="border-brand-light-gray absolute top-[calc(50%-16px)] right-3 z-20 cursor-pointer rounded-full border bg-white p-2 sm:right-5 md:top-1/3 md:right-10 md:p-4 lg:right-15"
        onClick={handlePlayToggle}
        aria-label={isPlaying ? 'Pause' : 'Play'}
      >
        {isPlaying ?
          <PauseIcon height={14} width={14} />
        : <PlayIcon height={14} width={14} />}
      </button>
    </div>
  );
};
